import { BlurFilter, IDestroyOptions, Point, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { UpgradeResourcePackage } from "../resources/UpgradeResourcePackage";
import { CircleScrollItem } from "./CircleScrollItem";
import { DropShadowFilter } from "pixi-filters";
import { GameFont } from "../../../common/GameFont";
import { IUpgradeBuildingModel } from "../../../common/models/Building/upgrade/IUpgradeBuildingModel";
import { nameof } from "../../../../../shared/bindable-data";
import { gsap } from "gsap";
import { Event } from "event-system";

export class BuildingItem extends CircleScrollItem {
    public readonly onClick: Event<void>;

    private readonly upgradeItemModel: IUpgradeBuildingModel;
    private readonly upgradeResources: UpgradeResourcePackage;
    private readonly image: Sprite;
    private readonly upgradeNumberText: Text;
    private readonly lockedImage: Sprite;
    private readonly frame: Sprite;
    private readonly blur: BlurFilter;
    private readonly blueGlow: DropShadowFilter;
    private readonly orangeGlow: DropShadowFilter;
    private readonly greenGlow: DropShadowFilter;
    private itemState: BuildingItemState;

    private set state(state: BuildingItemState) {
        this.processState(state);
    }

    public get upgradeModel(): IUpgradeBuildingModel {
        return this.upgradeItemModel;
    }

    public set current(selected: boolean) {
        if (this.itemState != BuildingItemState.NEED_TO_FIX && this.itemState != BuildingItemState.DONE) {
            this.currentGlow = selected;
        }
        const upgradeNumberTextAvailable = !selected && this.itemState != BuildingItemState.LOCKED;
        const animations = new gsap.core.Timeline;
        animations.to(this.upgradeNumberText, { alpha: upgradeNumberTextAvailable ? 1 : 0, duration: 1.2 });
        animations.then(() => animations.kill());
    }

    private set currentGlow(enabled: boolean) {
        this.blueGlow.enabled = enabled;
        const animations = new gsap.core.Timeline;
        animations.to(this.blueGlow, { alpha: enabled ? 1 : 0, duration: 1.2 });
        animations.then(() => animations.kill());
    }

    private set needToFixGlow(enabled: boolean) {
        this.orangeGlow.enabled = enabled;
    }

    private set doneGlow(enabled: boolean) {
        this.greenGlow.enabled = enabled;
    }

    private set bluredBackground(enabled: boolean) {
        this.blur.enabled = enabled;
    }

    public constructor(
        itemTexture: Texture,
        upgradeResources: UpgradeResourcePackage,
        upgradeModel: IUpgradeBuildingModel
    ) {
        super();
        this.upgradeResources = upgradeResources;
        this.onClick = new Event();

        this.upgradeItemModel = upgradeModel;
        this.upgradeItemModel.propertyChanged.add(this.onUpgradeItemModelPropertyChanged, this);

        this.image = new Sprite(itemTexture);
        this.addChild(this.image);

        this.upgradeNumberText = new Text("0/4", new WhiteTextStyle());
        this.upgradeNumberText.position.set(this.image.width * 0.5 - this.upgradeNumberText.width * 0.5, this.image.height + 8);
        this.addChild(this.upgradeNumberText);

        this.frame = new Sprite(upgradeResources.assets_views_upgrade_upgrade_cards_elements_fix_label_png);
        this.frame.position.set(this.image.width * 0.5 - this.frame.width * 0.5, this.image.height * 0.5 - this.frame.height * 0.5);
        this.frame.visible = false;
        this.addChild(this.frame);

        this.lockedImage = new Sprite(upgradeResources.assets_views_upgrade_upgrade_cards_elements_lock_label_png);
        this.lockedImage.position.set(this.image.width * 0.5 - this.lockedImage.width * 0.5, this.image.height * 0.5 - this.lockedImage.height * 0.5);
        this.lockedImage.visible = false;
        this.addChild(this.lockedImage);

        this.itemState = BuildingItemState.DEFAULT;

        this.blur = new BlurFilter(5);
        this.blur.enabled = false;
        this.blueGlow = new DropShadowFilter({
            offset: new Point(0, 0),
            blur: 18,
            color: 0x44DDFF,
            quality: 4.5,
            alpha: 1,
            pixelSize: 0.5
        });
        this.blueGlow.enabled = false;
        this.orangeGlow = new DropShadowFilter({
            offset: new Point(0, 0),
            blur: 18,
            color: 0xFF6E0E,
            quality: 4.5,
            alpha: 1,
            pixelSize: 0.5
        });
        this.orangeGlow.enabled = false;
        this.greenGlow = new DropShadowFilter({
            offset: new Point(0, 0),
            blur: 18,
            color: 0xA3FF51,
            quality: 4.5,
            alpha: 1,
            pixelSize: 0.5
        });
        this.greenGlow.enabled = false;
        this.image.filters = [this.blur, this.blueGlow, this.orangeGlow, this.greenGlow];

        this.on("pointerdown", this.onTouchStart, this);
        this.eventMode = "dynamic";

        this.updateCurrentState();
    }

    private onTouchStart(): void {
        this.onClick.emit();
    }

    private async onUpgradeItemModelPropertyChanged(model: IUpgradeBuildingModel, key: string): Promise<void> {
        switch (key) {
            case nameof(model, "lock"):
                if (model.lock.value.status) {
                    this.state = BuildingItemState.LOCKED;
                }
                break;
            case nameof(model, "elements"):
                if (model.lock.value.status) {
                    return;
                }
                if (model.elements.value.needToFix > 0) {
                    this.state = BuildingItemState.NEED_TO_FIX;
                } else {
                    this.state = model.elements.value.needToUpgrade === 0 ? BuildingItemState.DONE : BuildingItemState.DEFAULT;
                }
                break;
        }
    }

    public updateCurrentState(): void {
        this.onUpgradeItemModelPropertyChanged(this.upgradeItemModel, "lock");
        this.onUpgradeItemModelPropertyChanged(this.upgradeItemModel, "elements");
    }

    private processState(state: BuildingItemState): void {
        switch (state) {
            case BuildingItemState.DEFAULT: {
                this.processDefaultState();
                break;
            }
            case BuildingItemState.LOCKED: {
                this.processLockedState();
                break;
            }
            case BuildingItemState.NEED_TO_FIX: {
                this.processNeedToFixState();
                break;
            }
            case BuildingItemState.DONE: {
                this.processDoneState();
                break;
            }
        }
    }

    private processDefaultState(): void {
        this.itemState = BuildingItemState.DEFAULT;
        this.upgradeNumberText.style = new WhiteTextStyle();
        this.frame.visible = false;
        this.needToFixGlow = false;
        this.doneGlow = false;
        this.lockedImage.visible = false;
        this.bluredBackground = false;
        this.upgradeNumberText.visible = true;
    }

    private processLockedState(): void {
        this.itemState = BuildingItemState.LOCKED;
        this.frame.visible = false;
        this.needToFixGlow = false;
        this.doneGlow = false;
        this.lockedImage.visible = true;
        this.bluredBackground = true;
        this.upgradeNumberText.visible = false;
    }

    private processNeedToFixState(): void {
        this.itemState = BuildingItemState.NEED_TO_FIX;
        this.upgradeNumberText.style = new OrangeTextStyle();
        this.frame.texture = this.upgradeResources.assets_views_upgrade_upgrade_cards_elements_fix_label_png;
        this.frame.visible = true;
        this.needToFixGlow = true;
        this.doneGlow = false;
        this.lockedImage.visible = false;
        this.bluredBackground = false;
        this.upgradeNumberText.visible = true;
    }

    private processDoneState(): void {
        this.itemState = BuildingItemState.DONE;
        this.upgradeNumberText.style = new GreenTextStyle();
        this.frame.texture = this.upgradeResources.assets_views_upgrade_upgrade_cards_elements_doneBg_png;
        this.frame.visible = true;
        this.needToFixGlow = false;
        this.doneGlow = true;
        this.lockedImage.visible = false;
        this.bluredBackground = false;
        this.upgradeNumberText.visible = true;
    }

    public updateItemsNumber(progressPoints: number, maxPoints: number): void {
        this.upgradeNumberText.text = `${progressPoints}/${maxPoints}`;
    }

    public hideText(): void {
        this.upgradeNumberText.visible = false;
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.off("touchstart", this.onTouchStart, this);
        this.upgradeItemModel.propertyChanged.remove(this.onUpgradeItemModelPropertyChanged, this);
        super.destroy(options);
    }
}

export enum BuildingItemState {
    DEFAULT,
    LOCKED,
    NEED_TO_FIX,
    DONE
}

class WhiteTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 32,
            lineHeight: 38.4,
            align: "center",
            fill: "#FFFFFF"
        })
    }
}

class OrangeTextStyle extends WhiteTextStyle {
    public constructor() {
        super();
        this.fill = [
            "#FFBF05",
            "#FF6D0E"
        ]
    }
}

class GreenTextStyle extends WhiteTextStyle {
    public constructor() {
        super();
        this.fill = [
            "#AAFD09",
            "#16CF15"
        ]
    }
}
